import { notEmpty } from "@product/scmp-sdk";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import { useContentItemProviders } from "~/components/content/content-item-render/hooks";
import type { ContentItemRenderVariantProps } from "~/components/content/content-item-render/types";
import { useContentSponsor } from "~/components/content/content-sponsor-tag/hooks";
import type { Variant as TopicLinkVariant } from "~/components/content/content-topic-link";
import type { primaryContentItemContent$key } from "~/queries/__generated__/primaryContentItemContent.graphql";

import {
  Container,
  Content,
  Cover,
  Footer,
  Header,
  LinkContainer,
  LiveTagContainer,
  StyledEntityLink,
} from "./styles";

export type Props = {
  reference: primaryContentItemContent$key;
  topicLinkVariant?: TopicLinkVariant;
} & ContentItemRenderVariantProps;

export const ContentItemPrimary = forwardRef<HTMLDivElement, Props>(
  ({ className, reference: reference_, topicLinkVariant }, reference) => {
    const entity = useFragment(
      graphql`
        fragment primaryContentItemContent on Content {
          __typename
          ...entityLink
          ...hooksUseContentSponsorContent
          ...hooksContentItemProvidersContent
            @arguments(
              withLiveTag: true
              withAuthorLink: true
              withTopicLink: true
              withHeadline: true
              withCoverImage: true
              withCoverImageSize1200x800: true
              withCoverImageSize540x360: true
              withVideoDurationOverlay: true
              withSubHeadline: true
              withActionBar: true
              withSponsorTag: true
            )
        }
      `,
      reference_,
    );
    const providers = useContentItemProviders(entity, topicLinkVariant);

    const header = (
      <>
        <LiveTagContainer>{providers.liveTag()}</LiveTagContainer>
        <LinkContainer>
          {providers.authorLink?.()}
          {providers.topicLink?.()}
        </LinkContainer>
        <StyledEntityLink reference={entity}>
          {providers.headline({
            hideTooltip: true,
            preferSocialHeadline: true,
          })}
        </StyledEntityLink>
      </>
    );
    const cover = (
      <StyledEntityLink reference={entity}>
        {providers.coverImage({
          responsiveVariants: {
            desktopUp: "size1200x800",
            mobileUp: "size540x360",
            tabletUp: "size540x360",
          },
        })}
        {providers.video?.durationOverlay({
          responsiveVariants: { desktopUp: "large", mobileUp: "large", tabletUp: "large" },
        })}
      </StyledEntityLink>
    );
    const isVideo = entity?.__typename === "Video";
    const content = isVideo ? null : providers.subHeadline();
    const contentSponsor = useContentSponsor(entity);
    const isSponsorArticle = notEmpty(contentSponsor?.name);
    const footer = isSponsorArticle
      ? providers.sponsorTag?.({
          color: "#001246",
          gap: "4px",
          layoutDirection: "horizontal",
          nameFontSize: "14px",
          typeFontSize: "14px",
        })
      : providers.actionBar();

    return (
      <Container className={className} ref={reference}>
        <Header>{header}</Header>
        <Cover>{cover}</Cover>
        <Content>{content}</Content>
        <Footer>{footer}</Footer>
      </Container>
    );
  },
);

ContentItemPrimary.displayName = "ContentItemPrimary";
